import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Grid, FormControl, InputLabel, Select,
  MenuItem, FormHelperText, Alert, Chip, CircularProgress, Paper,
  TextField, IconButton, Stepper, Step, StepLabel, Divider
} from '@mui/material';
import {
  Close as CloseIcon, CheckCircle as CheckCircleIcon,
  Business as BusinessIcon, Public as PublicIcon,
  Email as EmailIcon, Person as PersonIcon,
  Rocket as RocketIcon
} from '@mui/icons-material';
import plantillasApi from '../../../api/plantillasApi';
import configuracionApi from '../../../api/configuracionApi';
import empresasApi from '../../../api/empresasApi';
import usuariosApi from '../../../api/usuariosApi';

function DialogAprobarNegocio({ open, negocio, onClose, onAprobado }) {
  const [activeStep, setActiveStep] = useState(0);
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState('');
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaPreview, setPlantillaPreview] = useState(null);
  const [loadingPlantillas, setLoadingPlantillas] = useState(true);
  const [procesando, setProcesando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  const [datosAdmin, setDatosAdmin] = useState({
    nombre: '',
    email: '',
  });

  const steps = ['Datos del Admin', 'Plantilla', 'Confirmar'];

  useEffect(() => {
    if (open) {
      cargarPlantillas();
      setActiveStep(0);
      setMensaje(null);
      if (negocio) {
        setDatosAdmin({
          nombre: negocio.contacto_empresa || negocio.nombre || '',
          email: negocio.correo_contacto || negocio.email_empresa || '',
        });
      }
    }
  }, [open, negocio]);

  useEffect(() => {
    if (plantillaSeleccionada) {
      setPlantillaPreview(plantillas.find(p => p.id_plantilla === plantillaSeleccionada));
    } else {
      setPlantillaPreview(null);
    }
  }, [plantillaSeleccionada, plantillas]);

  const cargarPlantillas = async () => {
    try {
      setLoadingPlantillas(true);
      const response = await plantillasApi.getPlantillas();
      setPlantillas(response.data || []);
    } catch (error) {
      setMensaje({ type: 'error', text: 'Error al cargar plantillas' });
    } finally {
      setLoadingPlantillas(false);
    }
  };

  const validarPaso = () => {
    if (activeStep === 0) {
      if (!datosAdmin.nombre || !datosAdmin.email) {
        setMensaje({ type: 'error', text: 'Nombre y email son obligatorios' });
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(datosAdmin.email)) {
        setMensaje({ type: 'error', text: 'Email inválido' });
        return false;
      }
    }
    setMensaje(null);
    return true;
  };

  const handleNext = () => {
    if (validarPaso()) setActiveStep(prev => prev + 1);
  };

  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleAprobar = async () => {
    if (!negocio?.id) return;
    setProcesando(true);
    setMensaje(null);

    try {
      // 1. Aprobar negocio
      await empresasApi.aprobarNegocio(negocio.id);

      // 2. Aplicar plantilla
      if (plantillaSeleccionada) {
        await configuracionApi.aplicarPlantillaByEmpresa(negocio.id, plantillaSeleccionada);
      }

      // 3. Crear usuario admin (sin password, auth por código email)
      await usuariosApi.crear({
        username: datosAdmin.email,
        password_hash: 'auth_code_only',
        id_empresa: negocio.id,
        id_pais: negocio.id_pais,
        id_tipo_autenticacion: 3, // Código por correo
        id_nivel_usuario: 2,      // Admin Empresa
        is_active: true,
      });

      if (onAprobado) onAprobado();
      handleCerrar();
    } catch (error) {
      setMensaje({ type: 'error', text: error.response?.data?.error || 'Error al aprobar' });
    } finally {
      setProcesando(false);
    }
  };

  const handleCerrar = () => {
    if (!procesando) {
      setPlantillaSeleccionada('');
      setPlantillaPreview(null);
      setDatosAdmin({ nombre: '', email: '' });
      setMensaje(null);
      setActiveStep(0);
      onClose();
    }
  };

  const nombresEstados = {
    1: 'Pendiente', 2: 'Asignado', 3: 'En Camino', 4: 'En Proceso',
    5: 'Terminado', 6: 'En Espera', 7: 'Cancelado', 8: 'Firmado',
    9: 'Revisado', 10: 'Liquidado',
  };

  return (
    <Dialog open={open} onClose={handleCerrar} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      {/* Header */}
      <DialogTitle sx={{ borderBottom: '1px solid #E2E8F0', pb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={{ bgcolor: '#ECFDF5', p: 1, borderRadius: 2, display: 'flex' }}>
              <CheckCircleIcon sx={{ color: '#10B981', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>Aprobar Empresa</Typography>
              <Typography variant="body2" color="text.secondary">{negocio?.nombre}</Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCerrar} disabled={procesando}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {mensaje && (
          <Alert severity={mensaje.type} onClose={() => setMensaje(null)} sx={{ mb: 2 }}>
            {mensaje.text}
          </Alert>
        )}

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map(label => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {/* PASO 0: Datos del Admin */}
        {activeStep === 0 && (
          <Box>
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Datos de la empresa</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BusinessIcon fontSize="small" sx={{ color: '#64748B' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">RUC/Doc</Typography>
                      <Typography variant="body2" fontWeight={600}>{negocio?.ruc || '-'}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PublicIcon fontSize="small" sx={{ color: '#64748B' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">País</Typography>
                      <Typography variant="body2" fontWeight={600}>{negocio?.nombre_pais || '-'}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            <Typography variant="subtitle1" fontWeight={700} color="#1A237E" gutterBottom>
              Usuario administrador de la empresa
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Este usuario podrá acceder al panel de gestión. El acceso es con código por correo electrónico (sin contraseña).
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Nombre del administrador *" value={datosAdmin.nombre}
                  onChange={(e) => setDatosAdmin({ ...datosAdmin, nombre: e.target.value })}
                  disabled={procesando}
                  InputProps={{ startAdornment: <PersonIcon sx={{ mr: 1, color: '#94A3B8' }} /> }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Email del administrador *" type="email" value={datosAdmin.email}
                  onChange={(e) => setDatosAdmin({ ...datosAdmin, email: e.target.value })}
                  disabled={procesando}
                  helperText="Se enviará un correo de bienvenida con instrucciones de acceso"
                  InputProps={{ startAdornment: <EmailIcon sx={{ mr: 1, color: '#94A3B8' }} /> }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* PASO 1: Plantilla */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="#1A237E" gutterBottom>
              Plantilla de proceso
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Define los estados del flujo de trabajo que tendrá esta empresa.
            </Typography>

            {loadingPlantillas ? (
              <Box display="flex" justifyContent="center" py={3}><CircularProgress size={30} /></Box>
            ) : (
              <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Plantilla</InputLabel>
                  <Select value={plantillaSeleccionada} onChange={(e) => setPlantillaSeleccionada(e.target.value)} label="Plantilla">
                    <MenuItem value=""><em>Sin plantilla (configuración manual)</em></MenuItem>
                    {plantillas.map(p => (
                      <MenuItem key={p.id_plantilla} value={p.id_plantilla}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{p.nombre_plantilla}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {p.descripcion} — {p.estados_configurados?.length || 0} estados
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Define los estados de proceso para esta empresa</FormHelperText>
                </FormControl>

                {plantillaPreview?.estados_configurados && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" gutterBottom>Estados que se activarán:</Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                      {plantillaPreview.estados_configurados.map(id => (
                        <Chip key={id} label={nombresEstados[id] || `Estado ${id}`} size="small" color="primary" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* PASO 2: Confirmar */}
        {activeStep === 2 && (
          <Box>
            <Paper sx={{ p: 3, bgcolor: '#ECFDF5', borderRadius: 2, border: '1px solid #A7F3D0', mb: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <RocketIcon sx={{ color: '#10B981' }} />
                <Typography variant="subtitle1" fontWeight={700} color="#065F46">
                  Todo listo para aprobar
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Empresa</Typography>
                  <Typography variant="body2" fontWeight={600}>{negocio?.nombre}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">País</Typography>
                  <Typography variant="body2" fontWeight={600}>{negocio?.nombre_pais || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Admin</Typography>
                  <Typography variant="body2" fontWeight={600}>{datosAdmin.nombre}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Email admin</Typography>
                  <Typography variant="body2" fontWeight={600}>{datosAdmin.email}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Autenticación</Typography>
                  <Typography variant="body2" fontWeight={600}>Código por correo</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Plantilla</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {plantillaPreview?.nombre_plantilla || 'Sin plantilla'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Plan</Typography>
                  <Typography variant="body2" fontWeight={600}>Free Trial — 30 días, 5 técnicos, 240 servicios</Typography>
                </Grid>
              </Grid>
            </Paper>

            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Se enviará un correo de bienvenida a <strong>{datosAdmin.email}</strong> con instrucciones para empezar a usar TRACKEA.
            </Alert>
          </Box>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ borderTop: '1px solid #E2E8F0', px: 3, py: 2 }}>
        <Button onClick={handleCerrar} disabled={procesando} color="inherit">Cancelar</Button>
        <Box sx={{ flex: 1 }} />
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={procesando}>Atrás</Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={handleNext}
            sx={{ bgcolor: '#1A237E', '&:hover': { bgcolor: '#0D47A1' }, borderRadius: 2, fontWeight: 600 }}>
            Siguiente
          </Button>
        ) : (
          <Button variant="contained" onClick={handleAprobar} disabled={procesando}
            startIcon={procesando ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            sx={{
              bgcolor: '#10B981', '&:hover': { bgcolor: '#059669' },
              borderRadius: 2, fontWeight: 700, px: 3,
            }}>
            {procesando ? 'Aprobando...' : 'Aprobar y Notificar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default DialogAprobarNegocio;
